import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { inventoryApi as I, INV_STATUS_STYLE, GRADE_STYLE } from "@/lib/inventory";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Loader2, Inbox as InboxIcon } from "lucide-react";

export default function Inventory() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ statuses: [] });
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState("stock");
  const [inbox, setInbox] = useState([]);
  const [items, setItems] = useState([]);
  const [dispatchReady, setDispatchReady] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const canCreate = can("inventory", "create");

  const load = async () => {
    setLoading(true);
    try {
      const [s, ib, list, dr, lg] = await Promise.all([I.stats(), I.inbox(), I.items({ search: search || undefined, status }), I.items({ status: "Picked" }), I.ledger({})]);
      setStats(s); setInbox(ib); setItems(list); setDispatchReady(dr); setLedger(lg);
    } finally { setLoading(false); }
  };
  useEffect(() => { I.meta().then(setMeta); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const receiveAll = async () => {
    try { const r = await I.receive(null, null); toast.success(`Received ${r.received} roll(s) into stock`); await load(); setTab("stock"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const ItemTable = ({ data, emptyMsg }) => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow className="bg-slate-50">
          <TableHead className="text-xs uppercase text-slate-500">Item</TableHead>
          <TableHead className="text-xs uppercase text-slate-500">Roll / Package</TableHead>
          <TableHead className="text-xs uppercase text-slate-500">Customer</TableHead>
          <TableHead className="text-xs uppercase text-slate-500">Meters</TableHead>
          <TableHead className="text-xs uppercase text-slate-500">Location</TableHead>
          <TableHead className="text-xs uppercase text-slate-500">Grade</TableHead>
          <TableHead className="text-xs uppercase text-slate-500">Status</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {data.map((r) => (
            <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/inventory/items/${r.id}`)} data-testid={`item-row-${r.item_no}`}>
              <TableCell className="font-medium text-slate-800">{r.item_no}<span className="block text-xs text-slate-400">{r.so_no || r.job_no}</span></TableCell>
              <TableCell className="text-slate-700">{r.batch_id}</TableCell>
              <TableCell className="text-slate-700">{r.customer}</TableCell>
              <TableCell className="tabular-nums text-slate-700">{r.meters} m</TableCell>
              <TableCell className="text-slate-600 text-xs">{[r.location?.warehouse, r.location?.rack, r.location?.bin].filter(Boolean).join(" / ")}</TableCell>
              <TableCell>{r.qc_grade ? <Badge className={`hover:bg-inherit ${GRADE_STYLE[r.qc_grade]}`}>{r.qc_grade}</Badge> : "—"}</TableCell>
              <TableCell><Badge className={`hover:bg-inherit ${INV_STATUS_STYLE[r.status]}`}>{r.status}</Badge></TableCell>
            </TableRow>
          ))}
          {data.length === 0 && <TableRow><TableCell colSpan={7} className="py-12 text-center text-slate-400">{emptyMsg}</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Inventory</h2>
        <p className="mt-1 text-sm text-slate-500">Single source of truth for physical stock. Only QC-passed rolls enter. Every movement is ledgered.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="p-4" data-testid="inv-stat-available"><p className="text-xs uppercase tracking-wider text-slate-500">Available meters</p><p className="mt-1 font-heading text-2xl font-bold text-emerald-600">{(stats.available_meters ?? 0).toLocaleString("en-IN")}</p></Card>
        <Card className="p-4" data-testid="inv-stat-reserved"><p className="text-xs uppercase tracking-wider text-slate-500">Reserved meters</p><p className="mt-1 font-heading text-2xl font-bold text-blue-600">{(stats.reserved_meters ?? 0).toLocaleString("en-IN")}</p></Card>
        <Card className="p-4" data-testid="inv-stat-dispatchready"><p className="text-xs uppercase tracking-wider text-slate-500">Dispatch-ready</p><p className="mt-1 font-heading text-2xl font-bold text-violet-600">{stats.dispatch_ready ?? 0}</p></Card>
        <Card className="p-4" data-testid="inv-stat-onhand"><p className="text-xs uppercase tracking-wider text-slate-500">On-hand items</p><p className="mt-1 font-heading text-2xl font-bold text-slate-900">{stats.on_hand ?? 0}</p></Card>
      </div>

      <Card className="p-4">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div> : (
          <Tabs value={tab} onValueChange={setTab}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <TabsList data-testid="inv-tabs">
                <TabsTrigger value="inbox" data-testid="tab-inbox">Inbox {inbox.length ? `(${inbox.length})` : ""}</TabsTrigger>
                <TabsTrigger value="stock" data-testid="tab-stock">Stock</TabsTrigger>
                <TabsTrigger value="dispatch" data-testid="tab-dispatch">Dispatch-ready</TabsTrigger>
                <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger</TabsTrigger>
              </TabsList>
              {tab === "stock" && (
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" className="w-52 pl-9" data-testid="inv-search-input" />
                  </div>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-44" data-testid="inv-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All statuses</SelectItem>{meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <TabsContent value="inbox" className="mt-4">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm text-slate-500">QC-passed rolls waiting to be received into stock.</p>
                {canCreate && inbox.length > 0 && <Button onClick={receiveAll} data-testid="receive-all-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><InboxIcon className="mr-2 h-4 w-4" />Receive all</Button>}
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase text-slate-500">Roll / Package</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">QC</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">Customer</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">Meters</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">Grade</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {inbox.map((f) => (
                      <TableRow key={f.batch_id} data-testid={`inv-inbox-row-${f.batch_id}`}>
                        <TableCell className="font-medium text-slate-800">{f.batch_id}</TableCell>
                        <TableCell className="text-slate-700">{f.inspection_no}</TableCell>
                        <TableCell className="text-slate-700">{f.customer}</TableCell>
                        <TableCell className="tabular-nums text-slate-700">{f.packed_meters} m</TableCell>
                        <TableCell>{f.grade ? <Badge className={`hover:bg-inherit ${GRADE_STYLE[f.grade]}`}>{f.grade}</Badge> : "—"}</TableCell>
                      </TableRow>
                    ))}
                    {inbox.length === 0 && <TableRow><TableCell colSpan={5} className="py-12 text-center text-slate-400" data-testid="inv-inbox-empty">No QC-passed rolls awaiting receipt.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
            <TabsContent value="stock" className="mt-4"><ItemTable data={items} emptyMsg="No stock yet. Receive QC-passed rolls from the Inbox." /></TabsContent>
            <TabsContent value="dispatch" className="mt-4"><ItemTable data={dispatchReady} emptyMsg="No picked items ready for dispatch." /></TabsContent>
            <TabsContent value="ledger" className="mt-4">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase text-slate-500">When</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">Item</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">Movement</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">Δ meters</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">By</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">Ref / Reason</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {ledger.map((l) => (
                      <TableRow key={l.id} data-testid={`ledger-row-${l.id}`}>
                        <TableCell className="text-xs text-slate-500">{new Date(l.at).toLocaleString()}</TableCell>
                        <TableCell className="font-medium text-slate-800">{l.item_no}</TableCell>
                        <TableCell><Badge className="bg-slate-100 capitalize text-slate-700 hover:bg-slate-100">{l.type}</Badge> <span className="text-xs text-slate-400">{l.from_status}→{l.to_status}</span></TableCell>
                        <TableCell className={`tabular-nums ${l.meters_delta < 0 ? "text-rose-600" : l.meters_delta > 0 ? "text-emerald-600" : "text-slate-500"}`}>{l.meters_delta > 0 ? "+" : ""}{l.meters_delta}</TableCell>
                        <TableCell className="text-slate-600">{l.by}</TableCell>
                        <TableCell className="text-slate-500 text-xs">{[l.ref, l.reason].filter(Boolean).join(" · ")}</TableCell>
                      </TableRow>
                    ))}
                    {ledger.length === 0 && <TableRow><TableCell colSpan={6} className="py-12 text-center text-slate-400">No movements recorded yet.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </Card>
    </div>
  );
}
